// Modules
import React, { useCallback, useContext, useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

// Components
import LoadingError from 'components/utility/loading-error/LoadingError'

// Pages
import Error from 'pages/error/Error'
import PasswordChange from 'pages/password-change/PasswordChange'
import TermsOfService from 'pages/terms-of-service/TermsOfService'
import TwoFactorAuthentication from 'pages/two-factor-authentication/TwoFactorAuthentication'

// Hooks
import useAccount from 'hooks/account/useAccount'
import useRedirect from 'hooks/external/useRedirect'
import useToken from 'hooks/authentication/useToken'

// Context
import Data from 'context/Data'

// Helpers
import { checkIfTokenIsValid, logout } from 'helpers/authentication'
import { getActivePage } from 'helpers/route'
import { setSentryUser } from 'helpers/sentry'

// Constants
import { FALLBACK_URL, IS_ON_PREMISE } from 'constants/environment'

// Components (Local)
function Fallback () {
	window.location.href = FALLBACK_URL
	return null
}

// Data (Local)
const ALLOWED_PROTECTED_PAGES = ['2fa', 'password-change']

// Route: Protected
function Protected () {
	// Data
	useToken()
	useAccount(true)
	const { account } = useContext(Data)
	const { willRedirect } = useRedirect({ willSkip: !account?.isLatestTermsOfServiceAccepted || ALLOWED_PROTECTED_PAGES.includes(getActivePage()) })

	// Functions (Memoized)
	const logoutIfUnauthorized = useCallback(() => {
		if (document.visibilityState === 'visible') {
			if (!checkIfTokenIsValid()) logout()
		}
	}, [])

	// Effects
	useEffect(() => {
		document.addEventListener('visibilitychange', logoutIfUnauthorized)
		return () => document.removeEventListener('visibilitychange', logoutIfUnauthorized)
	}, [logoutIfUnauthorized])
	useEffect(() => {
		if (!!account?.id && !IS_ON_PREMISE) {
			setSentryUser({
				email: account.email,
				id: account.id,
				username: `${account.firstName} ${account.lastName}`
			})
		}
	}, [account?.email, account?.firstName, account?.id, account?.lastName])

	// Render
	if (willRedirect) return null
	if (!!account?.hasLoadingError) return <LoadingError />
	if (typeof(account?.isLatestTermsOfServiceAccepted) !== 'boolean') return null
	return account.isLatestTermsOfServiceAccepted
		? <Routes>
			{/* Root (Fixed) */}
			<Route
				path="/"
				element={<Fallback />} />
			{/* Password change */}
			<Route
				path="password-change"
				element={<PasswordChange />} />
			{/* Two-factor authentication */}
			<Route
				path="2fa"
				element={<TwoFactorAuthentication />} />
			{/* Login */}
			<Route
				path="login"
				element={<Navigate to="/" />} />
			{/* Register */}
			<Route
				path="register"
				element={<Navigate to="/" />} />
			{/* Reset */}
			<Route
				path="reset"
				element={<Navigate to="/" />} />
			{/* Verification */}
			<Route
				path="verification"
				element={<Navigate to="/" />} />
			{/* Fallback (Fixed) */}
			<Route
				path="*"
				element={<Error />} />
		</Routes>
		: <Routes>
			{/* Any */}
			<Route
				path="*"
				element={<TermsOfService />} />
		</Routes>
}

// Export
export default Protected
